/**
 * Pharmacy Sales + Bills + Payments + Refunds API client.
 * Base: /inventory/pharmacy/...  (trailing slash enforced by inventory-request)
 * NEVER send patient_id — backend resolves patient context server-side.
 */
import client from './inventory-request';
import {
  getPharmacyActorId,
  getPharmacyActorName,
  getCashierContext,
  withTenant,
} from '../hooks/usePharmacyRole';

const BASE = '/inventory/pharmacy';

// ─── Sales ───────────────────────────────────────────────────────────────────

/** POST /inventory/pharmacy/sales/ */
export function createSale(body) {
  return client.post(`${BASE}/sales/`, body);
}

/** POST /inventory/pharmacy/sales/{sale_id}/finalize/ */
export function finalizeSale(saleId, body = {}, scope = {}) {
  const actor = getPharmacyActorId();
  return client.post(
    `${BASE}/sales/${saleId}/finalize/`,
    { finalized_by: body.finalized_by || actor, remarks: body.remarks },
    withTenant({}, scope),
  );
}

/** GET /inventory/pharmacy/sales/{sale_id}/ */
export function getSale(saleId, scope = {}) {
  return client.get(`${BASE}/sales/${saleId}/`, withTenant({}, scope));
}

/**
 * PATCH /inventory/pharmacy/sales/{sale_id}/ — edit a DRAFT basket in place.
 * The counter must never re-POST a sale to change its lines: a new sale id
 * orphans any controlled-drug authorization already queued against the old one.
 */
export function updateSale(saleId, body, scope = {}) {
  const actor = getPharmacyActorId();
  return client.patch(
    `${BASE}/sales/${saleId}/`,
    { updated_by: actor, ...body },
    withTenant({}, scope),
  );
}

/**
 * POST /inventory/pharmacy/sales/{sale_id}/request-controlled-auth/
 * Idempotent catch-up. The service queues controlled lines on create/update,
 * so this is only a repair for a draft that somehow has none.
 */
export function requestSaleControlledAuth(saleId, body = {}, scope = {}) {
  const actor = getPharmacyActorId();
  return client.post(
    `${BASE}/sales/${saleId}/request-controlled-auth/`,
    { requested_by: body.requested_by || actor, reason: body.reason || null },
    withTenant({}, scope),
  );
}

/**
 * DELETE /inventory/pharmacy/sales/{sale_id}/ — discard an abandoned DRAFT order.
 * The service cancels its PENDING controlled-drug requests too, so the
 * pharmacist stops seeing a sale nobody intends to complete.
 */
export function deleteSale(saleId, scope = {}) {
  return client.delete(`${BASE}/sales/${saleId}/`, withTenant({}, scope));
}

/** GET /inventory/pharmacy/sales/{sale_id}/items/ */
export function getSaleItems(saleId, scope = {}) {
  return client.get(`${BASE}/sales/${saleId}/items/`, withTenant({}, scope));
}

// ─── Bills ───────────────────────────────────────────────────────────────────

/** POST /inventory/pharmacy/bills/ */
export function createBill(body) {
  return client.post(`${BASE}/bills/`, body);
}

/** POST /inventory/pharmacy/bills/{bill_id}/price/ */
export function priceBill(billId, scope = {}) {
  return client.post(`${BASE}/bills/${billId}/price/`, {}, withTenant({}, scope));
}

/** POST /inventory/pharmacy/bills/{bill_id}/discount/ */
export function applyBillDiscount(billId, body, scope = {}) {
  return client.post(`${BASE}/bills/${billId}/discount/`, body, withTenant({}, scope));
}

/** POST /inventory/pharmacy/bills/{bill_id}/approve-discount/ */
export function approveBillDiscount(billId, body, scope = {}) {
  return client.post(`${BASE}/bills/${billId}/approve-discount/`, body, withTenant({}, scope));
}

/** POST /inventory/pharmacy/bills/{bill_id}/recheck/ */
export function recheckBill(billId, body = {}, scope = {}) {
  const actor = getPharmacyActorId();
  return client.post(
    `${BASE}/bills/${billId}/recheck/`,
    { rechecked_by: body.rechecked_by || actor },
    withTenant({}, scope),
  );
}

/** POST /inventory/pharmacy/bills/{bill_id}/finalize/ */
export function finalizeBill(billId, body = {}, scope = {}) {
  const actor = getPharmacyActorId();
  return client.post(
    `${BASE}/bills/${billId}/finalize/`,
    { finalized_by: body.finalized_by || actor },
    withTenant({}, scope),
  );
}

/** GET /inventory/pharmacy/bills/ */
export function listBills(params = {}, scope = {}) {
  return client.get(`${BASE}/bills/`, withTenant(params, scope));
}

/** GET /inventory/pharmacy/bills/{bill_id}/ */
export function getBill(billId, scope = {}) {
  return client.get(`${BASE}/bills/${billId}/`, withTenant({}, scope));
}

// ─── Payment ─────────────────────────────────────────────────────────────────

/** POST /inventory/pharmacy/bills/{bill_id}/payment/ */
export function submitBillPayment(billId, body, scope = {}) {
  const cashier = getCashierContext();
  return client.post(
    `${BASE}/bills/${billId}/payment/`,
    {
      currency: 'INR',
      ...cashier,
      ...body,
      created_by: body.created_by || cashier.created_by,
    },
    withTenant({}, scope),
  );
}

/** GET /inventory/pharmacy/bills/{bill_id}/payment/ — 404 means no payment yet (expected). */
export async function getBillPayment(billId, scope = {}) {
  try {
    return await client.get(`${BASE}/bills/${billId}/payment/`, withTenant({}, scope));
  } catch (err) {
    if (err?.status === 404) return null;
    throw err;
  }
}

// ─── Refunds ─────────────────────────────────────────────────────────────────

/** POST /inventory/pharmacy/bills/{bill_id}/refunds/ */
export function raiseRefund(billId, body, scope = {}) {
  const actor = getPharmacyActorId();
  return client.post(
    `${BASE}/bills/${billId}/refunds/`,
    { ...body, created_by: body.created_by || actor },
    withTenant({}, scope),
  );
}

/** GET /inventory/pharmacy/bills/{bill_id}/refunds/ */
export function listBillRefunds(billId, scope = {}) {
  return client.get(`${BASE}/bills/${billId}/refunds/`, withTenant({}, scope));
}

/** POST /inventory/pharmacy/bills/{bill_id}/refunds/{refund_id}/action/ */
export function actionRefund(billId, refundId, body, scope = {}) {
  const actor = getPharmacyActorId();
  return client.post(
    `${BASE}/bills/${billId}/refunds/${refundId}/action/`,
    { actioned_by: body.actioned_by || actor, ...body },
    withTenant({}, scope),
  );
}

/** POST /inventory/pharmacy/bills/{bill_id}/refunds/{refund_id}/settle/ */
export function settleRefund(billId, refundId, body, scope = {}) {
  const actor = getPharmacyActorId();
  return client.post(
    `${BASE}/bills/${billId}/refunds/${refundId}/settle/`,
    { actioned_by: body.actioned_by || actor, ...body },
    withTenant({}, scope),
  );
}

/**
 * POST /inventory/pharmacy/bills/{bill_id}/refunds/{refund_id}/exchange/
 * Settles an approved refund against a finalized replacement bill (facilities
 * with cash_refund_prohibited). The server prices the replacement bill and
 * decides what, if anything, the customer still owes.
 */
export function exchangeRefund(billId, refundId, body, scope = {}) {
  const { cashier_id, cashier_name, shift_id, counter_id } = getCashierContext();
  return client.post(
    `${BASE}/bills/${billId}/refunds/${refundId}/exchange/`,
    {
      actioned_by: getPharmacyActorId(),
      cashier_id,
      cashier_name,
      shift_id,
      counter_id,
      ...body,
    },
    withTenant({}, scope),
  );
}

/** GET /inventory/pharmacy/refund-policy/ → { cash_refund_prohibited } */
export function getRefundPolicy() {
  return client.get(`${BASE}/refund-policy/`);
}

/** GET /inventory/pharmacy/refunds/?status=… */
export function listRefunds(params = {}, scope = {}) {
  return client.get(`${BASE}/refunds/`, withTenant(params, scope));
}

// ─── Payload builders (no patient_id) ────────────────────────────────────────

/**
 * Build sale create body from wizard batch lines.
 * One items[] entry per selected batch.
 */
export function buildSalePayload({
  lines,
  storeId,
  scope,
  remarks,
  transactionDate,
  patientDisplay = null,
}) {
  const actor = getPharmacyActorId();

  const asMoney = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(4) : '0.0000';
  };

  const items = (lines || []).flatMap((line) => {
    const med = line.medicine;
    const raw = med?._raw || {};
    // The unit the quantities below are counted in. Batch lines are entered in
    // the item's issue/dispense UOM, so that is what has to travel with them —
    // stock-ledger converts to base on the way in. Sending a base uom_id with
    // a strip quantity would debit ten times too little; converting here and
    // still sending the strip uom_id would debit ten times too much.
    const uomId = med?.issue_uom_id
      ?? raw.issue_uom_id
      ?? raw.base_uom_id
      ?? raw.uom_id
      ?? med?.base_uom_id
      ?? med?.uom_id
      ?? null;

    return (line.batchLines || [])
      .filter((bl) => Number(bl.qty) > 0)
      .map((bl) => {
        const rawTax = raw.tax_percent;
        const item = {
          item_id: line.medicine_id || med?.item_id,
          // The receipt the counter ticked. Without it stock-ledger falls back
          // to its own FEFO walk, and a line billed against one batch takes its
          // stock out of another — at that other batch's pack size.
          grn_item_id: bl.grn_item_id || null,
          batch_number: bl.batch_number || null,
          expiry_date: bl.expiry_date || null,
          requested_quantity: asMoney(bl.qty),
          unit_rate: asMoney(bl.rate ?? 0),
          tax_percent: rawTax != null && rawTax !== '' ? asMoney(rawTax) : '0.0000',
          discount_percent: asMoney(bl.discount_percent ?? 0),
        };
        if (uomId != null && uomId !== '') item.uom_id = uomId;
        return item;
      });
  });

  const body = {
    tenant_id: scope.tenant_id,
    hospital_id: scope.hospital_id,
    encounter_type: 'OP',
    from_store_id: storeId,
    transaction_date: transactionDate,
    remarks: remarks || '',
    created_by: actor,
    items,
  };
  const payload = buildSaleTransactionPayload(patientDisplay);
  if (payload) body.transaction_payload = payload;
  // Omit null optional fields — some serializers 500 on explicit nulls.
  return body;
}

/**
 * transaction_payload for a counter sale. The backend reads patient_name here
 * to decide whether a controlled line may ride on a walk-in, and freezes it
 * into the Controlled Queue snapshot — so an empty name must stay absent
 * rather than be sent as ''.
 */
export function buildSaleTransactionPayload(patientDisplay) {
  if (!patientDisplay) return null;
  const name = String(patientDisplay.name || '').trim();
  const mobile = String(patientDisplay.mobile || patientDisplay.phone || '').trim();
  const payload = {};
  if (name) payload.patient_name = name;
  if (mobile) payload.patient_mobile = mobile;
  return Object.keys(payload).length ? payload : null;
}

/** Stable identity of a sale line: one row per medicine + receipt.
 *
 *  Keyed on grn_item_id, not batch_number. Two receipts of a non-batch-tracked
 *  medicine both carry NULL in the label, so keying on it collapsed them into
 *  one entry — the diff then updated one line twice and deleted the other.
 */
function saleLineKey(row) {
  const batch = row?.grn_item_id || row?.batch_number || '';
  return `${row?.item_id || ''}|${batch}`;
}

const QTY_FIELDS = ['requested_quantity', 'unit_rate', 'tax_percent', 'discount_percent'];

/**
 * Diff the basket against the lines the draft sale already holds, so a change
 * becomes a PATCH rather than a new sale. Returns null when nothing differs —
 * the caller skips the request entirely.
 */
export function buildSaleDiff(saleItems, desiredItems) {
  const existing = new Map();
  (saleItems || []).forEach((row) => {
    const id = row.transaction_item_id || row.inventory_transaction_item_id || row.id;
    if (id) existing.set(saleLineKey(row), { ...row, id });
  });

  const addItems = [];
  const updateItems = [];
  const seen = new Set();

  (desiredItems || []).forEach((item) => {
    const key = saleLineKey(item);
    const match = existing.get(key);
    if (!match) {
      addItems.push(item);
      return;
    }
    seen.add(key);
    const changed = QTY_FIELDS.filter(
      (f) => item[f] != null && Number(item[f]) !== Number(match[f]),
    );
    if (changed.length) {
      updateItems.push({
        id: match.id,
        ...Object.fromEntries(changed.map((f) => [f, item[f]])),
      });
    }
  });

  const deleteItemIds = [];
  existing.forEach((row, key) => {
    if (!seen.has(key)) deleteItemIds.push(row.id);
  });

  if (!addItems.length && !updateItems.length && !deleteItemIds.length) return null;
  return {
    add_items: addItems,
    update_items: updateItems,
    delete_item_ids: deleteItemIds,
  };
}

/**
 * Build bill create body from sale items.
 * Uses search_name / search_mobile / patient_age / patient_gender only — never patient_id.
 */
export function buildBillPayload({
  scope,
  store,
  storeId,
  transactionItemIds,
  patientDisplay = {},
  taxInclusive = true,
}) {
  const actor = getPharmacyActorId();
  const storeName = store?.store_name || store?.name || '';
  const body = {
    tenant_id: scope.tenant_id,
    hospital_id: scope.hospital_id,
    bill_type: 'CASH',
    billing_location_id: storeId,
    store_id: storeId,
    store_name: storeName,
    pricing_basis: 'SALE',
    tax_inclusive: taxInclusive,
    item_discount_percent: '0',
    margin_percent: '0',
    line_transaction_item_ids: transactionItemIds,
    dispense_type: 'OPD',
    place_of_supply: null,
    invoice_type: 'B2C_SMALL',
    priority: 'NORMAL',
    created_by: actor,
  };

  if (patientDisplay.name) body.search_name = patientDisplay.name;
  if (patientDisplay.mobile || patientDisplay.phone) {
    body.search_mobile = patientDisplay.mobile || patientDisplay.phone;
  }
  if (patientDisplay.age !== '' && patientDisplay.age != null) {
    // Serializer rejects a number here: "body.patient_age: Input should be a valid string".
    body.patient_age = String(patientDisplay.age);
  }
  if (patientDisplay.gender) body.patient_gender = patientDisplay.gender;

  return body;
}

export { getPharmacyActorId, getPharmacyActorName, getCashierContext };
